// MegaCom (KG) subscription flow for the KG landing pages (1089, 3346): SMS code via the MegaCom DCB gateway
// (send-pin / verify-pin / plans). Beeline numbers never touch this file - they keep using
// the subscription engine through common/ApiSender.
//
// The gateway always answers 200 with { message, status, code, data } - `status` is the single
// source of truth for success.
import { logger } from './utils/logger';
import { toNationalNumber } from './msisdn';
import { isFailedVerify } from './failureRouting';

// Override per environment (e.g. a same-origin proxy path) without touching the code.
const GTW_BASE_URL = process.env.REACT_APP_MEGACOM_GTW_BASE_URL || 'https://megagtw.kid-security.io';

const SERVICE_ABBREVIATION = 'KS';
// Landing-page name sent to the gateway, per LP (defaults to 1089's).
const DEFAULT_LANDING_PAGE_NAME = 'KG 1089 LP - MegaCom';

// Daily plan shown until GET /plans answers, or if the gateway can't be reached at all.
export const FALLBACK_DAILY_PLAN = { planId: 1, price: 6, freeDays: 3, renewalEveryDays: 1, validityDays: 1 };

export const OTP_LENGTH = 4;

async function request(method, path, payload) {
  try {
    const response = await fetch(`${GTW_BASE_URL}${path}`, {
      method,
      headers: {
        'Accept-Language': 'ru',
        ...(payload ? { 'Content-Type': 'application/json' } : {}),
      },
      body: payload ? JSON.stringify(payload) : undefined,
    });
    const result = await response.json().catch(() => null);
    return { ok: response.ok, result };
  } catch (error) {
    return { ok: false, result: null, error };
  }
}

const isSuccess = (response) => Boolean(response?.ok && response.result?.status === true);

// The gateway's own message is shown to the visitor when it is in Russian (e.g. "Ваша подписка
// приостановлена"); the plans endpoint answers in Kyrgyz regardless of Accept-Language, so a
// Kyrgyz message falls back to a generic Russian one.
const SEND_PIN_FAILED_MSG = 'Не удалось отправить код, попробуйте позже';
const VERIFY_PIN_FAILED_MSG = 'Не удалось подтвердить подписку, попробуйте ещё раз';

function userMessage(response, fallback) {
  const message = response?.result?.message;
  return message && !/[үөңҮӨҢ]/.test(message) ? message : fallback;
}

function basePayload(msisdn, planId, lpName) {
  return {
    msisdn: `996${toNationalNumber(msisdn)}`,
    planId,
    language: 'RU',
    lpUrl: window.location.origin + window.location.pathname,
    lpName,
    queryParams: window.location.search.length > 1 ? window.location.search.substring(1) : null,
    service: SERVICE_ABBREVIATION,
  };
}

export async function getDailyPlan() {
  const op = logger.startOperation('get_plans', 'getPlans', {}, 'Fetching plans from the MegaCom gateway');
  const response = await request('GET', '/plans');

  if (!isSuccess(response)) {
    op.fail(response.error || new Error(response.result?.message || 'plans rejected'), 'Fetching plans failed, using the built-in daily plan');
    return FALLBACK_DAILY_PLAN;
  }

  const plans = Array.isArray(response.result?.data) ? response.result.data : [];
  const daily = plans.find((plan) => plan.renewalEveryDays === 1 && plan.validityDays != null);
  op.success(daily ? `Daily plan ${daily.planId}: ${daily.price} som` : 'No daily plan in the response, using the built-in one');
  return daily || FALLBACK_DAILY_PLAN;
}

export async function sendPin({ msisdn, planId, lpName = DEFAULT_LANDING_PAGE_NAME }) {
  const op = logger.startOperation(
    'send_otp',
    'sendPin',
    { msisdn, planId, lpName },
    `Visitor clicked "Subscribe" for MegaCom number ${msisdn} on "${lpName}"`
  );

  const response = await request('POST', '/send-pin', basePayload(msisdn, planId, lpName));

  if (isSuccess(response)) {
    op.success(`OTP sent successfully to ${msisdn}`);
    return { success: true };
  }

  op.fail(
    response.error || new Error(response.result?.message || 'send-pin rejected'),
    `OTP send ${response.error ? 'threw an error' : 'rejected'} for ${msisdn}${response.result?.message ? `: ${response.result.message}` : ''}`
  );
  return { success: false, message: userMessage(response, SEND_PIN_FAILED_MSG) };
}

export async function verifyPin({ msisdn, otp, planId, lpName = DEFAULT_LANDING_PAGE_NAME }) {
  const op = logger.startOperation(
    'verify_otp',
    'verifyPin',
    { msisdn, planId, lpName },
    `Visitor submitted an OTP for MegaCom number ${msisdn} on "${lpName}"`
  );

  const response = await request('POST', '/verify-pin', { ...basePayload(msisdn, planId, lpName), otp });

  if (isSuccess(response)) {
    op.success(`OTP verified successfully for ${msisdn}`);
    return { success: true };
  }

  op.fail(
    response.error || new Error(response.result?.message || 'verify-pin rejected'),
    `OTP verification ${response.error ? 'threw an error' : 'rejected'} for ${msisdn}${response.result?.message ? `: ${response.result.message}` : ''}`
  );
  // `failed`: the rejection is final (not a wrong/expired code), so the LP should go to /failed.
  return { success: false, failed: isFailedVerify(response.result?.code), message: userMessage(response, VERIFY_PIN_FAILED_MSG) };
}
